import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import {
  AccountsService,
  ObservationService,
  RolesService,
  SettingsService,
  ShiftService,
  TeamsService,
  UserdataService,
  UserService
} from '@services';

import { translatedNoteValue } from '@services/noteTranslation/note-translation';
import { DetailReportTableService } from '@modules/reporting/services/detail-report-table.service';
import { cloneDeep, each, filter, find, forEach, get, isArray, join, map, round, sortBy, uniq } from 'lodash';
import { firstTaggedFor, taggedUserIDs, taggedUserTeam } from '@services/observations/tagged-users';
import { Observation, ObservationSubtypes, ObservationTypes } from '@services/observations/observation.interfaces';
import { CustomFieldData, CustomFieldType } from '@modules/customFields/custom-fields.interfaces';
import { CustomFieldsService } from '@modules/customFields/services';

@Injectable({
  providedIn: 'root'
})
export class ObsDetailModelService {
  public static CustomFieldValuesKey = 'custom_field';

  private columnOptions = [
    {
      id: 'observationID',
      description: this.translate.instant('DASHPAGES.SUPERVISER_COMP_open_data0'),
      label: this.translate.instant('DASHPAGES.SUPERVISER_COMP_open_data0'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = collection[0].observationID;
        }
        return label;
      }
    },
    {
      id: 'value',
      description: this.translate.instant('MGMT_DETAILS.Custom_Fields'),
      label: this.translate.instant('MGMT_DETAILS.Custom_Fields'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => this.translate.instant('SHARED.None'),
      multipleColumnHandler: (observation: Observation, columnsRef: Object) => {
        this.buildCustomFields(observation, columnsRef);
      },
    },
    {
      id: 'location',
      description: this.translate.instant('SHARED.Location'),
      label: this.translate.instant('SHARED.Location'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = get(this.userService.findLocation(collection[0].locationID), 'name');
          if (!label) {
            label = this.translate.instant('DASHPAGES.NA');
          }

        }
        return label;
      }
    },
    {
      id: 'zone',
      description: this.translate.instant('SHARED.Zone'),
      label: this.translate.instant('SHARED.Zone'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const locRef = this.userService.findLocation(collection[0].locationID);
          const zoneRef = this.userService.findAnyZone(locRef, collection[0].zoneID);
          if (zoneRef) {
            label = `${locRef.name}: ${zoneRef.name}`;
          } else {
            if (locRef) {
              label = locRef.name + '/Site-wide';
            } else {
              label = 'Site-wide';
            }
          }
        }
        return label;
      }
    },
    {
      id: 'type',
      description: this.translate.instant('SHARED.Observation_Type'),
      label: this.translate.instant('SHARED.Observation_Type'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = collection[0].type;
          if (label === 'condition') {
            label = this.translate.instant('SHARED.Unsafe_Condition');
          } else if (label === 'behavior') {
            label = this.translate.instant('SHARED.Coaching_Opportunity');
          } else if (label === 'quality') {
            label = this.translate.instant('SHARED.CHANGING_TYPE_TS_quality');
          } else if (label === 'pi') {
            label = this.translate.instant('REPORTING.EDIT_pi');
            const sub = collection[0].subtype;
            if (sub === 'waiting') {
              label += ' - ' + this.translate.instant('SHARED.Waiting');
            } else {
              label += ' - ' + this.translate.instant('SHARED.General_Improvement');
            }
          } else if (label === 'compliment') {
            label = this.translate.instant('SHARED.Thumbs-Up');
          } else if (label === 'ca') {
            label = this.translate.instant('SHARED.Corrective_Actions');
          } else if (label === 'si') {
            label = this.translate.instant('SHARED.Submission');
          } else if (label === 'ai') {
            label = this.translate.instant('SHARED.Asset_Issues');
          }
        }
        return label;
      }
    },
    {
      id: 'subtype',
      description: this.translate.instant('SHARED.Observation_Subtype'),
      label: this.translate.instant('SHARED.Observation_Subtype'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        const subtype = collection?.[0]?.subtype;
        const subtypeMap = {
          [ObservationSubtypes.PIGeneral]: this.translate.instant('SHARED.General_Improvement'),
          [ObservationSubtypes.PIWaiting]: this.translate.instant('SHARED.Waiting'),
          [ObservationSubtypes.QualityProduction]: this.translate.instant('SHARED.Production'),
          [ObservationSubtypes.QualityReceiving]: this.translate.instant('SHARED.Receiving'),
          [ObservationSubtypes.QualityRMA]: this.translate.instant('SHARED.RMA')
        };
    
        return this.translate.instant(subtypeMap[subtype] || 'SHARED.None');
      }
    },
    {
      id: 'status',
      description: this.translate.instant('SHARED.Status'),
      label: this.translate.instant('SHARED.Status'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = collection[0].state;
          if ((collection[0].type === 'pi' && collection[0].subtype !== 'general') || collection[0].type === 'coaching' || collection[0].type === 'compliment' || collection[0].type === 'behavior') {
            label = this.translate.instant('SHARED.Closed');
          } else {
            if (label === 'new' || label === 'escalated') {
              label = this.translate.instant('SHARED.Open');
            } else if (label === 'workorder') {
              label = this.translate.instant('SHARED.Work_Order');
            } else if (label === 'fixed') {
              label = this.translate.instant('SHARED.Fixed');
            } else if (label === 'resolved') {
              label = this.translate.instant('SHARED.Closed');
            }
          }
        }
        return label;
      }
    },
    {
      id: 'severity',
      description: this.translate.instant('SHARED.Severity'),
      label: this.translate.instant('SHARED.Severity'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = collection[0].severity;
        }
        return label;
      }
    },
    {
      id: 'likelihood',
      description: this.translate.instant('SHARED.Likelihood'),
      label: this.translate.instant('SHARED.Likelihood'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = collection[0].likelihood;
        }
        return label;
      }
    },
    {
      id: 'creator',
      description: this.translate.instant('SHARED.Creator'),
      label: this.translate.instant('SHARED.Creator'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = this.userService.getFullname(collection[0].userID);
        }
        return label;
      }
    },
    {
      id: 'creatorRole',
      description: this.translate.instant('SHARED.Creator_Role'),
      label: this.translate.instant('SHARED.Creator_Role'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const roles = this.accountsService.getByID(collection[0].userID).roles;
          if (roles?.length) {
            const role = collection[0]?.roles?.length ? collection[0]?.roles[0] : roles[0];
            label = get(this.roleService.getRoleById(role), 'name');
          }
        }
        return label;
      }
    },
    {
      id: 'recipients',
      description: this.translate.instant('SHARED.Recipients'),
      label: this.translate.instant('SHARED.Recipients'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (isArray(collection)) {
          const recips = collection[0].recipients;
          const names = [];
          each(recips, id => {
            names.push(this.userService.getFullname(id));
          });
          label = join(sortBy(names), ', ');
        }
        return label;
      }
    },
    // behavior "tagged users" (CVX-6668)
    {
      id: 'taggedUsers',
      description: this.translate.instant('SHARED.Tagged_Users'),
      label: this.translate.instant('SHARED.Tagged_Users'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        const obs = isArray(collection) ? collection[0] : null;
        const names = obs?.type === 'behavior' ? map(taggedUserIDs(obs), id => this.userService.getFullname(id)) : [];
        return names.length ? join(sortBy(names), ', ') : this.translate.instant('SHARED.None');
      }
    },
    {
      id: 'taggedUsersTeams',
      description: this.translate.instant('SHARED.Tagged_Users_Teams'),
      label: this.translate.instant('SHARED.Tagged_Users_Teams'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        const obs = isArray(collection) ? collection[0] : null;
        const teams = obs?.type === 'behavior'
          ? uniq(filter(map(taggedUserIDs(obs), id => taggedUserTeam(id, (uid) => this.accountsService.getByID(uid)))))
          : [];
        return teams.length ? join(sortBy(map(teams, team => this.teamsService.teamNameByID(team))), ', ') : this.translate.instant('SHARED.None');
      }
    },
    {
      id: 'owner',
      description: this.translate.instant('SHARED.Owner'),
      label: this.translate.instant('SHARED.Owner'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          label = this.userService.getFullname(collection[0].ownerID);
        }
        return label;
      }
    },
    {
      id: 'workOrderNumber',
      description: this.translate.instant('SHARED.Work_Order'),
      label: this.translate.instant('SHARED.Work_Order'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]?.workorder) {
          label = collection[0].workorder;
        }
        return label;
      }
    },
    {
      id: 'creatorTeam',
      description: this.translate.instant('SHARED.Creator_Team'),
      label: this.translate.instant('SHARED.Creator_Team'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const u = this.accountsService.getAccount(collection[0].userID);
          if (u?.primaryGroup) {
            label = this.teamsService.teamNameByID(u.primaryGroup);
          } else if (u?.groups && u.groups[0]) {
            label = this.teamsService.teamNameByID(u.groups[0]);
          }
        }
        return label;
      }
    },
    {
      id: 'ownerTeam',
      description: this.translate.instant('REPORTING.EDIT_Owner_Team'),
      label: this.translate.instant('REPORTING.EDIT_Owner_Team'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const u = this.accountsService.getAccount(collection[0].ownerID);
          if (u?.primaryGroup) {
            label = this.teamsService.teamNameByID(u.primaryGroup);
          } else if (u && u.groups && u.groups[0]) {
            label = this.teamsService.teamNameByID(u.groups[0]);
          }
        }
        return label;
      }
    },
    {
      id: 'ownerRole',
      description: this.translate.instant('REPORTING.EDIT_Owner_Role'),
      label: this.translate.instant('REPORTING.EDIT_Owner_Role'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const owner = this.accountsService.getAccount(collection[0].ownerID);
          if (owner?.roles?.length) {
            label = '';
            map(owner.roles, (roleID) => {
              const role = find(this.roleService.roles.data, <any>{roleID});
              label += label ? `, ${role?.name}` : role?.name;
            });
          }
        }

        return label;
      }
    },
    {
      id: 'notes',
      description: this.translate.instant('MGMT_DETAILS.Notes'),
      label: this.translate.instant('MGMT_DETAILS.Notes'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const notes = collection[0].notes;
          if (notes && notes.length) {
            label = join(map(notes, (note) => this.noteText(note)), '<br />');
          }
        }
        return label;
      }
    },
    {
      id: 'dateCreated',
      description: this.translate.instant('SHARED.Date_Created'),
      label: this.translate.instant('SHARED.Date_Created'),
      cellType: 'string',
      showWhen: true,
      func: (collection, row, type) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const ref = collection[0];
          if (type === 'sort') {
            label = ref.created;
          } else if (type === 'export') {
            label = this.detailReportTableService.getExcelFormatDate(collection?.[0]?.created);
          } else {
            label = this.userService.locationDateTimeFormat(ref.locationID, ref.created, this.translate.instant('SHARED.None'));
          }
        }
        return label;
      }
    },
    {
      id: 'dateFixed',
      description: this.translate.instant('SHARED.Date_Fixed'),
      label: this.translate.instant('SHARED.Date_Fixed'),
      cellType: 'string',
      showWhen: true,
      func: (collection, row, type) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const ref = collection[0];
          const fixed = find(collection[0].history, {activity: 'fixed'});
          if (fixed) {
            const theTime = get(fixed, 'time');
            if (type === 'sort') {
              label = theTime;
            } else if (type === 'export') {
              label = this.detailReportTableService.getExcelFormatDate( theTime );
            } else {
              label = this.userService.locationDateTimeFormat(ref.locationID, theTime, this.translate.instant('SHARED.None'));
            }
          }
        }
        return label;
      }
    },
    {
      id: 'dateClosed',
      description: this.translate.instant('SHARED.Date_Closed'),
      label: this.translate.instant('SHARED.Date_Closed'),
      cellType: 'string',
      showWhen: true,
      func: (collection, row, type) => {
        let label = this.translate.instant('SHARED.None');
        if (collection[0]) {
          const resolved = find(collection[0].history, {activity: 'resolved'});
          if (resolved) {
            const ref = collection[0];
            const theTime = get(resolved, 'time');
            if (type === 'sort') {
              label = get(resolved, 'time');
            } else if (type === 'export') {
              label = this.detailReportTableService.getExcelFormatDate( theTime );
            } else {
              label = this.userService.locationDateTimeFormat(ref.locationID, theTime, this.translate.instant('SHARED.None'));
            }
          }
        }
        return label;
      }
    },
    {
      id: 'categories',
      description: this.translate.instant('SHARED.Categories'),
      label: this.translate.instant('SHARED.Categories'),
      cellType: 'string',
      showWhen: true,
      func: (collection) => {
        let label: string = '';
        if (collection[0]) {
          const categories = collection[0].categories;
          if (categories && categories.length) {
            const typeMap = {
              [ObservationTypes.Condition]: 'categories',
              [ObservationTypes.Behavior]: 'behaviors',
              [ObservationTypes.Quality]: 'qualityCats',
              [ObservationTypes.PI]: 'piCats',
              [ObservationTypes.Compliment]: 'compliments'
            };
            forEach(categories, (id: number) => {
              const category = this.settingsService.getItem(typeMap[collection[0].type], id);
              if (category) {
                label += category?.messageTitle + '<br />';
              }
            });
          }
        }
        return label || this.translate.instant('SHARED.None');
      }
    }
  ];
  private fieldOptions = [
    {
      id: 'observationID',
      description: this.translate.instant('SHARED.Response_ID'),
      label: this.translate.instant('SHARED.Response_ID'),
      fieldName: 'observationID',
      fieldType: 'string',
      fieldRequired: true,
      fieldFunc: (val, ref) => {
        const title = val;
        const obs = this.observations.getObservation(ref.observationID);
        // if (obs && obs.title) {
        //   title = obs.title;
        // }
        return [val, title];
      }
    },
    {
      id: 'categories',
      description: this.translate.instant('SHARED.Category'),
      label: this.translate.instant('SHARED.Category'),
      fieldName: 'categories',
      fieldType: 'array',
      fieldRequired: true,
      fieldFunc: (val, ref) => {
        let l = null;
        let b = null;
        if (val === null) {
          val = 0;
          l = 'None Assigned';
        } else {
          if (ref.type === 'condition') {
            b = this.settingsService.categories.data;
          } else if (ref.type === 'behavior') {
            b = this.settingsService.behaviors.data;
          } else if (ref.type === 'compliment') {
            b = this.settingsService.compliments.data;
          } else if (ref.type === 'quality') {
            b = this.settingsService.qualityCats.data;
          } else if (ref.type === 'pi') {
            b = this.settingsService.piCats.data;
          }
          const r: any = find(b, {messageID: val});
          if (r) {
            l = r.messageTitle;
          } else {
            // l = 'Missing Category ' + val;
            // val = 0;
            return null;
          }
        }
        return [val, l];
      }
    },
    {
      id: 'type',
      description: this.translate.instant('SHARED.Observation_Type'),
      label: 'Type',
      fieldName: 'type',
      fieldType: 'string',
      fieldFunc: (val, ref) => {
        let label = val;
        if (val === 'condition') {
          label = this.translate.instant('SHARED.Unsafe_Condition');
        } else if (val === 'quality') {
          label = this.translate.instant('SHARED.Quality');
        } else if (val === 'pi') {
          if (ref.subtype === 'waiting') {
            label = this.translate.instant('SHARED.Waiting');
          } else {
            label = this.translate.instant('SHARED.General_Improvement');
          }
        } else if (val === 'compliment') {
          label = this.translate.instant('SHARED.Thumbs-Up');
        } else if (val === 'behavior') {
          label = this.translate.instant('SHARED.Coaching_Opportunity');
        } else if (val === 'ca') {
          label = this.translate.instant('SHARED.Corrective_Action');
        } else if (val === 'si') {
          label = this.translate.instant('SHARED.Opportunity');
        } else if (label === 'ai') {
          label = this.translate.instant('SHARED.Asset_Issues');
        }

        return [val, label];
      }
    },
    {
      id: 'state',
      description: this.translate.instant('SHARED.Status'),
      label: this.translate.instant('SHARED.Status'),
      fieldName: 'state',
      fieldType: 'string',
      fieldFunc: (val, ref) => {
        let label = val;
        if ((ref.type === 'pi' && ref.subtype !== 'general') || ref.type === 'coaching' || ref.type === 'compliment' || ref.type === 'behavior') {
          val = 'closed';
          label = this.translate.instant('SHARED.Closed');
        } else {
          if (val === 'new' || val === 'escalated') {
            val = 'open';
            label = this.translate.instant('SHARED.Open');
          } else if (val === 'workorder') {
            label = this.translate.instant('SHARED.Work_Order');
          } else if (val === 'fixed') {
            label = this.translate.instant('SHARED.Fixed');
          } else if (val === 'resolved') {
            label = this.translate.instant('SHARED.Closed');
          }
        }
        return [val, label];
      }
    },
    {
      id: 'zone',
      description: this.translate.instant('SHARED.Zone'),
      label: this.translate.instant('SHARED.Zone'),
      fieldName: 'zoneID',
      fieldType: 'integer',
      fieldFunc: (val, ref) => {
        // find the zone in the location
        const zsig = ref.locationID + ':' + ref.zoneID;
        const locRef = this.userService.findLocation(ref.locationID);
        const zoneRef = this.userService.findAnyZone(locRef, ref.zoneID);
        if (zoneRef) {
          return [zsig, `${locRef.name}: ${zoneRef.name}`];
        } else {
          if (locRef) {
            return [zsig, locRef.name + '/Site-wide'];
          } else {
            return [zsig, 'Site-wide'];
          }
        }
      }
    },
    {
      id: 'creator',
      description: this.translate.instant('SHARED.Creator'),
      label: this.translate.instant('SHARED.Creator'),
      fieldName: 'userID',
      fieldType: 'integer',
      fieldFunc: (val) => [val, this.userService.getFullname(val)]
    },
    {
      id: 'creatorTeam',
      description: this.translate.instant('SHARED.Creator_Team'),
      label: this.translate.instant('SHARED.Team'),
      fieldName: 'userID',
      fieldType: 'integer',
      fieldFunc: (val) => {
        const u = this.accountsService.getAccount(val);
        if (u?.primaryGroup) {
          return [u.primaryGroup, this.teamsService.teamNameByID(u.primaryGroup)];
        } else if (u.groups && u.groups[0]) {
          return [u.groups[0], this.teamsService.teamNameByID(u.groups[0])];
        } else {
          return [0, this.translate.instant('SHARED.None')];
        }
      }
    },
    {
      id: 'owner',
      description: this.translate.instant('SHARED.Helper'),
      label: this.translate.instant('SHARED.Helper'),
      fieldName: 'ownerID',
      fieldType: 'integer',
      fieldFunc: (val) => {
        let n = 'unassigned';
        if (val) {
          n = this.userService.getFullname(val);
        }
        return [val, n];
      }
    },
    {
      id: 'ownerTeam',
      description: this.translate.instant('SHARED.Helper_Team'),
      label: this.translate.instant('SHARED.Team'),
      fieldName: 'ownerID',
      fieldType: 'integer',
      fieldFunc: (val) => {
        const u = this.accountsService.getAccount(val);
        if (u?.primaryGroup) {
          return [u.primaryGroup, this.teamsService.teamNameByID(u.primaryGroup)];
        } else if (u.groups && u.groups[0]) {
          return [u.groups[0], this.teamsService.teamNameByID(u.groups[0])];
        } else {
          return [0, this.translate.instant('SHARED.None')];
        }
      }
    },
    {
      id: 'locationID',
      description: this.translate.instant('SHARED.Location'),
      label: 'Location',
      fieldName: 'locationID',
      fieldType: 'number',
      fieldFunc: (locationId: number) => {
        let locationName: string = get(this.userService.findLocation(locationId), 'name');

        if (!locationName) {
          locationId = 0;
          locationName = this.translate.instant('DASHPAGES.NA');
        }
        return [locationId, locationName];
      }
    },
    {
      id: 'fixer',
      description: this.translate.instant('SHARED.Fixer'),
      label: this.translate.instant('SHARED.Fixer'),
      fieldName: 'userID',
      fieldType: 'integer',
      fieldFunc: (val, ref) => {
        let v = 0;
        let n = 'n/a';
        let p: any;
        if (ref.type === 'quality') {
          if (ref.subtype === 'receiving') {
            // receiving just go straight to closed; use the creator
            p = find(ref.history, {activity: 'created'});
          } else if (ref.subtype === 'production') {
            // production can go to dropped; pretend that is 'fixed'
            p = find(ref.history, {activity: 'fixed'});
            if (!p) {
              p = find(ref.history, {activity: 'dropped'});
            }
          }
        } else if (ref.type === 'pi') {
          if (ref.subtype === 'waiting') {
            p = find(ref.history, {activity: 'created'});
          } else {
            p = find(ref.history, {activity: 'fixed'});
          }
        } else {
          p = find(ref.history, {activity: 'fixed'});
        }

        // we still have no pointer - let's look for resolved
        if (!p) {
          p = find(ref.history, {activity: 'resolved'});
        }

        if (p) {
          v = p.userID;
          n = this.userService.getFullname(p.userID);
        }
        return [v, n];
      }
    },
    {
      id: 'fixerTeam',
      description: this.translate.instant('SHARED.Fixer_Team'),
      label: this.translate.instant('SHARED.Team'),
      fieldName: 'userID',
      fieldType: 'integer',
      fieldFunc: (val, ref) => {
        let v = val;
        let n = 'n/a';
        let p: any;
        if (ref.type === 'quality') {
          if (ref.subtype === 'receiving') {
            // receiving just go straight to closed; use the creator
            p = find(ref.history, {activity: 'created'});
          } else if (ref.subtype === 'production') {
            // production can go to dropped; pretend that is 'fixed'
            p = find(ref.history, {activity: 'fixed'});
            if (!p) {
              p = find(ref.history, {activity: 'dropped'});
            }
          }
        } else if (ref.type === 'pi') {
          if (ref.subtype === 'waiting') {
            p = find(ref.history, {activity: 'created'});
          } else {
            p = find(ref.history, {activity: 'fixed'});
          }
        } else {
          p = find(ref.history, {activity: 'fixed'});
        }

        // we still have no pointer - let's look for resolved
        if (!p) {
          p = find(ref.history, {activity: 'resolved'});
        }
        if (p) {
          const u = this.accountsService.getAccount(v);
          if (u?.primaryGroup) {
            return [u.primaryGroup, this.teamsService.teamNameByID(u.primaryGroup)];
          } else if (u.groups && u.groups[0]) {
            return [u.groups[0], this.teamsService.teamNameByID(u.groups[0])];
          } else {
            return [0, this.translate.instant('SHARED.None')];
          }
        }
        return [v, n];
      }
    },
    {
      id: 'closer',
      description: this.translate.instant('SHARED.Closer'),
      label: this.translate.instant('SHARED.Closer'),
      fieldName: 'userID',
      fieldType: 'integer',
      fieldFunc: (val, ref) => {
        let v = val;
        let n = 'n/a';
        const p: any = find(ref.history, {activity: 'resolved'});
        if (p) {
          v = p.userID;
          n = this.userService.getFullname(p.userID);
          return [v, n];
        }
      }
    },
    {
      id: 'team',
      description: this.translate.instant('SHARED.Team'),
      label: this.translate.instant('SHARED.Team'),
      fieldName: 'groupID',
      fieldType: 'integer',
      fieldFunc: (val) => [val, this.teamsService.teamNameByID(val)]
    },
    {
      id: 'tag',
      description: this.translate.instant('SHARED.Tag'),
      label: this.translate.instant('SHARED.Tag'),
      fieldName: 'tags',
      fieldType: 'array',
      fieldFunc: (val) => {
        const tag: any = get(find(this.settingsService.customTags.data, <any>{tagID: +val}), 'tag', null);
        return tag ? [val, tag] : null;
      }
    },
    // behavior "tagged users" (CVX-6668); a compliment's recipients are not tagged users
    {
      id: 'taggedUser',
      description: this.translate.instant('SHARED.Tagged_Users'),
      label: this.translate.instant('SHARED.Tagged_Users'),
      fieldName: 'recipients',
      fieldType: 'array',
      fieldFunc: (val, ref) => (ref.type === 'behavior' && +val) ? [+val, this.userService.getFullname(+val)] : null
    },
    {
      id: 'taggedUserTeam',
      description: this.translate.instant('SHARED.Tagged_Users_Teams'),
      label: this.translate.instant('SHARED.Team'),
      fieldName: 'recipients',
      fieldType: 'array',
      fieldFunc: (val, ref) => {
        const teamOf = (id: number) => taggedUserTeam(id, (uid) => this.accountsService.getByID(uid));
        if (ref.type !== 'behavior' || !+val || !firstTaggedFor(ref, val, teamOf)) {
          return null;
        }
        const team = teamOf(+val);
        return [team, team ? this.teamsService.teamNameByID(team) : this.translate.instant('SHARED.None')];
      }
    },
    {
      id: 'taggedUserSupervisor',
      description: this.translate.instant('SHARED.Tagged_User_Supervisor'),
      label: this.translate.instant('SHARED.Supervisor'),
      fieldName: 'recipients',
      fieldType: 'array',
      fieldFunc: (val, ref) => {
        const supervisorOf = (id: number) => Number(this.accountsService.getByID(id)?.supervisorID) || 0;
        if (ref.type !== 'behavior' || !+val || !firstTaggedFor(ref, val, supervisorOf)) {
          return null;
        }
        const supervisor = supervisorOf(+val);
        return [supervisor, supervisor ? this.userService.getFullname(supervisor) : this.translate.instant('SHARED.None')];
      }
    },
    {
      id: 'creator_shift',
      description: this.translate.instant('SHARED.Creator_Shift'),
      label: this.translate.instant('SHARED.Creator_Shift'),
      fieldName: 'creatorShift',
      fieldType: 'string',
      fieldFunc: (val) => [val, this.shift.name(val)]
    },
    {
      id: 'helper_shift',
      description: this.translate.instant('SHARED.Helper_Shift'),
      label: this.translate.instant('SHARED.Helper_Shift'),
      fieldName: 'ownerShift',
      fieldType: 'string',
      fieldFunc: (val) => [val, this.shift.name(val)]
    },
  ];

  constructor(
    private translate: TranslateService,
    private userService: UserService,
    private accountsService: AccountsService,
    private shift: ShiftService,
    private teamsService: TeamsService,
    private settingsService: SettingsService,
    private observations: ObservationService,
    private roleService: RolesService,
    private detailReportTableService: DetailReportTableService,
    private customFieldsService: CustomFieldsService,
    private userdataService: UserdataService
  ) {
  }

  /**
   * CVX-6218: an observation note carries its own `translations` list, whose
   * `value` is the translated string. At most one entry, absent when there is
   * nothing to serve — so the fallback is the untouched original. Reports show
   * the translation only; the Show original toggle is a detail-page affordance
   * (NoteListItemComponent) and has nowhere to live in an exported cell.
   */
  private noteText(note): string {
    return translatedNoteValue(note, this.userdataService.getLanguage());
  }

  public getColumns() {
    const columns = filter(cloneDeep(this.columnOptions), (opt) => opt.showWhen);
    return columns;
  }

  public getFieldOptions() {
    return this.fieldOptions;
  }

  private toDays(seconds, precision: number = 0) {
    let f = seconds;
    if (f) {
      f = round(f / (60 * 60 * 24), precision);
    }
    return f;
  }

  private buildCustomFields(observation: Observation, columnsRef: Object, key = ObsDetailModelService.CustomFieldValuesKey) {
    const customFields = this.observations.getSettingsByType(ObservationTypes.Quality, ObservationSubtypes.QualityRMA)?.customFields?.data || [];
    const fields = this.customFieldsService.buildFields(customFields);
    const values = this.observations.getCustomValues(observation) as CustomFieldData[];

    each(values, (value) => {
      const columnKey = `${key}_${value.uuid}`;
      const field = find(fields, { key: value.uuid });

      if (field?.type && !columnsRef?.[columnKey]) {
        const fieldType = (field?.props?.attributes?.originalType || field.type) as CustomFieldType;

        columnsRef[columnKey] = {
          id: columnKey,
          title: field?.props?.label,
          fromID: 'value',
          func: (data, { items }) => {
            const currentField = this.observations.getCustomValues(items?.[0], value.uuid) as CustomFieldData;
            return currentField?.value ? this.customFieldsService.parseFieldValue(currentField.value, fieldType, undefined, field) : '';
          }
        };
      }
    });
  }
}
